import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, X, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const EMPTY_BRAND = {
    name_en: "", name_ar: "", color: "#FFFFFF", accent_color: "",
    accent_len: 1, accent_pos: "first", italic: false, wide: false,
    weight: "bold", featured: false, order: 0,
};

export const BrandsPanel = () => {
    const [brands, setBrands] = useState([]);
    const [editing, setEditing] = useState(null); // null | {form}

    const load = useCallback(() => {
        api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [load]);

    const remove = async (b) => {
        if (!window.confirm(`حذف ماركة «${b.name_ar}»؟`)) return;
        try {
            await api.delete(`/brands/${b.id}`);
            toast.success("تم حذف الماركة");
            load();
        } catch {
            toast.error("تعذر الحذف");
        }
    };

    const inputCls =
        "bg-[#0A1228]/70 border border-white/10 rounded-xl py-2.5 px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300";

    return (
        <div data-testid="admin-brands-panel">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-display font-bold text-xl text-white">شعارات الماركات</h2>
                    <p className="text-sm text-slate-500 mt-1">الماركة المميزة تظهر ببطاقة كبيرة في الموقع</p>
                </div>
                <button
                    onClick={() => setEditing({ ...EMPTY_BRAND, order: brands.length + 1 })}
                    data-testid="admin-add-brand-button"
                    className="flex items-center gap-2 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-full px-5 py-2.5 hover:bg-amber-300 transition-colors duration-300"
                >
                    <Plus className="w-4 h-4" /> إضافة ماركة
                </button>
            </div>

            {editing && (
                <BrandForm
                    initial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { load(); setEditing(null); }}
                    inputCls={inputCls}
                />
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {brands.map((b) => (
                    <div key={b.id} className="flex items-center justify-between border border-white/10 rounded-2xl px-4 py-3.5 bg-[#101B3D]/60" data-testid={`brand-row-${b.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                            <span
                                className="font-bold text-sm truncate"
                                style={{ color: b.color, fontStyle: b.italic ? "italic" : "normal" }}
                                dir="ltr"
                            >
                                {b.name_en}
                            </span>
                            <span className="text-[11px] text-slate-500 truncate">{b.name_ar}</span>
                            {b.featured && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400 shrink-0" />}
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                            <button
                                onClick={() => setEditing({ accent_pos: "first", weight: "bold", ...b })}
                                data-testid={`brand-edit-${b.id}`}
                                className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-amber-400 hover:border-amber-400/50 transition-colors duration-200"
                                aria-label="تعديل"
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                                onClick={() => remove(b)}
                                data-testid={`brand-delete-${b.id}`}
                                className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-500/50 transition-colors duration-200"
                                aria-label="حذف"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

function BrandForm({ initial, onClose, onSaved, inputCls }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(initial.id);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = { ...form, accent_len: Number(form.accent_len), order: Number(form.order) };
        delete payload.id;
        try {
            if (isEdit) {
                await api.put(`/brands/${initial.id}`, payload);
                toast.success("تم تحديث الماركة");
            } else {
                await api.post("/brands", payload);
                toast.success("تمت إضافة الماركة");
            }
            onSaved();
        } catch {
            toast.error("تعذر الحفظ");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={submit} className="spotlight-card border border-amber-400/30 rounded-2xl p-5 mb-6" data-testid="brand-form">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-white">{isEdit ? "تعديل ماركة" : "ماركة جديدة"}</h3>
                <button type="button" onClick={onClose} className="text-slate-500 hover:text-white" aria-label="إغلاق">
                    <X className="w-4 h-4" />
                </button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
                <input required value={form.name_en} onChange={(e) => set("name_en", e.target.value)} placeholder="الاسم (إنجليزي)" data-testid="brand-name-en" className={inputCls} dir="ltr" />
                <input required value={form.name_ar} onChange={(e) => set("name_ar", e.target.value)} placeholder="الاسم (عربي)" data-testid="brand-name-ar" className={inputCls} />
                <input type="number" value={form.order} onChange={(e) => set("order", e.target.value)} placeholder="الترتيب" data-testid="brand-order" className={inputCls} dir="ltr" />
                <label className="flex items-center gap-2 text-xs text-slate-300">
                    <span>لون الاسم</span>
                    <input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="brand-color" className="w-10 h-9 rounded-lg bg-transparent border border-white/10 cursor-pointer" />
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-300">
                    <span>لون التمييز</span>
                    <input type="color" value={form.accent_color || "#FF0000"} onChange={(e) => set("accent_color", e.target.value)} data-testid="brand-accent-color" className="w-10 h-9 rounded-lg bg-transparent border border-white/10 cursor-pointer" />
                    <button type="button" onClick={() => set("accent_color", "")} className="text-[10px] text-slate-500 hover:text-red-400">بدون</button>
                </label>
                {form.accent_color && (
                    <>
                        <input type="number" min="1" value={form.accent_len} onChange={(e) => set("accent_len", e.target.value)} placeholder="عدد أحرف التمييز" data-testid="brand-accent-len" className={inputCls} dir="ltr" />
                        <select value={form.accent_pos} onChange={(e) => set("accent_pos", e.target.value)} data-testid="brand-accent-pos" className={inputCls}>
                            <option value="first">التمييز في البداية</option>
                            <option value="last">التمييز في النهاية</option>
                        </select>
                    </>
                )}
                <select value={form.weight} onChange={(e) => set("weight", e.target.value)} data-testid="brand-weight" className={inputCls}>
                    <option value="bold">عريض</option>
                    <option value="extrabold">عريض جداً</option>
                    <option value="black">أسود ثقيل</option>
                </select>
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={form.italic} onChange={(e) => set("italic", e.target.checked)} className="w-4 h-4 accent-amber-400" />
                    مائل
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="brand-featured" className="w-4 h-4 accent-amber-400" />
                    ماركة مميزة (بطاقة كبيرة)
                </label>
            </div>
            <div className="mt-4 flex items-center gap-4">
                <button type="submit" disabled={saving} data-testid="brand-form-submit" className="bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-xl px-6 py-2.5 hover:bg-amber-300 transition-colors duration-300 disabled:opacity-50">
                    {saving ? "جاري الحفظ..." : isEdit ? "حفظ التعديلات" : "إضافة الماركة"}
                </button>
                <span className="text-lg" dir="ltr" style={{ color: form.color, fontStyle: form.italic ? "italic" : "normal", letterSpacing: form.wide ? "0.18em" : "normal" }}>
                    {form.accent_color && form.accent_pos === "first" && <span style={{ color: form.accent_color }}>{form.name_en.slice(0, form.accent_len)}</span>}
                    {form.accent_color && form.accent_pos === "first" ? form.name_en.slice(form.accent_len) : form.accent_color ? form.name_en.slice(0, -form.accent_len) : form.name_en}
                    {form.accent_color && form.accent_pos === "last" && <span style={{ color: form.accent_color }}>{form.name_en.slice(-form.accent_len)}</span>}
                </span>
            </div>
        </form>
    );
}
